import { lazy } from "react";
import NotFoundPage from "./features/not-found/NotFoundPage.jsx";
import Shell, { HeaderLessShell, MiniShell } from "./features/shell/Shell.jsx";
import SparkShellWithWorkshopTourProvider from "./features/workshops/user-tour/SparkShellWithWorkshopTourProvider.jsx";
import DemoShell from "./features/demo/DemoShell.jsx";
import AuthShell from "./features/auth/NewAuth/AuthShell";
import { flags } from "./flags";

export const ROUTES = {
  empty: {
    scope: "public",
    allowMobile: true,
    path: "/",
    getUrl: () => "/",
    Component: lazy(() => import("./features/auth/Login/EmptyPage")),
    Layout: HeaderLessShell,
  },
  login: {
    scope: "public",
    allowMobile: true,
    path: "/login",
    getUrl: () => `/login`,
    Component: lazy(() =>
      flags.login
        ? import("./features/auth/NewAuth/AuthPage")
        : import("./features/auth/Login/LoginPage"),
    ),
    Layout: flags.login ? AuthShell : Shell,
  },
  loginIba: {
    scope: "public",
    allowMobile: true,
    path: "/login/iba-Fortbildung",
    getUrl: () => `/login/iba-Fortbildung`,
    Component: lazy(() => import("./features/one-off/iba/Login/LoginPage.jsx")),
    Layout: Shell,
  },
  registerPage: {
    scope: "public",
    allowMobile: true,
    path: "/r/:secret",
    getUrl: (secret) => `/r/${secret}`,
    Component: lazy(() =>
      flags.login
        ? import("./features/auth/NewAuth/AuthPage")
        : import("./features/auth/Login/RegisterPage"),
    ),
    Layout: flags.login ? AuthShell : Shell,
  },
  postRegisterPage: {
    scope: "user",
    allowMobile: true,
    path: "/register/:secret/success",
    getUrl: (secret) => `/register/${secret}/success`,
    Component: lazy(() =>
      flags.login
        ? import("./features/auth/NewAuth/RegisterSuccess/RegisterSuccess")
        : import("./features/auth/Login/Register/RegisterSuccess/RegisterSuccess.jsx"),
    ),
    Layout: flags.login ? AuthShell : MiniShell,
  },
  addSpark: {
    scope: "public",
    path: "/add-spark/:sparkSlug",
    getUrl: (sparkSlug) => `/add-spark/${sparkSlug}`,
    Component: lazy(() => import("./features/auto-add-spark/AddSparkPage.jsx")),
    Layout: MiniShell,
  },
  forgotPasswordPage: {
    scope: "public",
    allowMobile: true,
    path: "/auth/forgot-password/:token?",
    getUrl: () => `/auth/forgot-password`,
    Component: lazy(() =>
      flags.login
        ? import("./features/auth/NewAuth/PasswordReset/ForgotPasswordForTeachers")
        : import("./features/auth/PasswordReset/ForgotPasswordPage.jsx"),
    ),
    Layout: flags.login ? AuthShell : Shell,
  },
  forgotPasswordPageForStudents: {
    scope: "public",
    allowMobile: true,
    path: "/auth/how-to-reset-student-pw",
    getUrl: () => "/auth/how-to-reset-student-pw",
    Component: lazy(() =>
      flags.login
        ? import("./features/auth/NewAuth/PasswordReset/ForgotPasswordForStudents")
        : import("./features/auth/PasswordReset/ForgotPasswordPageForStudents.jsx"),
    ),
    Layout: flags.login ? AuthShell : Shell,
  },
  profile: {
    scope: "user",
    path: "/profile",
    getUrl: () => `/profile`,
    Component: lazy(() => import("./features/auth/Profile/Profile.jsx")),
    Layout: Shell,
  },

  workshopStart: {
    scope: "user",
    path: "/w/:slug",
    getUrl: (workshopSlug) => `/w/${workshopSlug}`,
    Component: lazy(
      () => import("./features/workshops/routing/RedirectToSpark.jsx"),
    ),
    Layout: Shell,
  },

  spark: {
    scope: "user",
    path: "/w/:workshopSlug/s/:sparkSlug/*",
    getUrl: ({ workshopSlug, sparkSlug, withTour = false }) =>
      `/w/${workshopSlug}/s/${sparkSlug}${withTour ? "?tour=true" : ""}`,
    Layout: SparkShellWithWorkshopTourProvider,
    noSuspense: true,
  },

  lernreisen: {
    scope: "user",
    path: "/lernreisen",
    getUrl: () => "/lernreisen",
    Layout: HeaderLessShell,
  },
  lernreisenOverview: {
    scope: "user",
    path: null,
    getUrl: () => "/lernreisen",
    Component: lazy(() => import("./features/workshops")),
    Layout: MiniShell,
    parent: "lernreisen",
    index: true,
  },
  lernreisenCreate: {
    scope: "user",
    path: "create",
    getUrl: () => "/lernreisen/create",
    Component: lazy(() =>
      flags.createWorkshop
        ? import("./features/workshops/new-create-workshop")
        : import("./features/workshops/create-workshop"),
    ),
    Layout: MiniShell,
    parent: "lernreisen",
  },

  admin: {
    scope: "admin",
    path: "/admin",
    getUrl: () => "/admin",
    Layout: Shell,
  },
  admins: {
    scope: "admin",
    path: "admins",
    getUrl: () => "/admin/admins",
    Component: lazy(() => import("./features/admin/AdminUsersPage")),
    Layout: MiniShell,
    parent: "admin",
  },
  templates: {
    scope: "admin",
    path: "templates",
    getUrl: () => "/admin/templates",
    Component: lazy(() => import("./features/admin/Templates.jsx")),
    Layout: MiniShell,
    parent: "admin",
  },
  administrateWorkshops: {
    scope: "admin",
    path: "workshops",
    getUrl: () => "/admin/workshops",
    Component: lazy(() => import("./features/admin/workshops/Workshops")),
    Layout: MiniShell,
    parent: "admin",
  },
  workshopDetail: {
    scope: "admin",
    path: ":slug",
    getUrl: (slug) => `/admin/workshops/${slug}`,
    Component: lazy(() => import("./features/admin/workshops/WorkshopDetail")),
    Layout: MiniShell,
    parent: "administrateWorkshops",
  },
  allComments: {
    scope: "admin",
    path: "moderate",
    getUrl: () => `/admin/moderate`,
    Component: lazy(() => import("./features/admin/ModerateComments")),
    Layout: MiniShell,
    parent: "admin",
  },
  managePublicShowrooms: {
    scope: "admin",
    path: "public-showrooms",
    getUrl: () => `/admin/public-showrooms`,
    Component: lazy(() => import("./features/admin/ManagePublicShowrooms")),
    Layout: MiniShell,
    parent: "admin",
  },

  demoHome: {
    scope: "public",
    path: "/demo",
    getUrl: () => "/demo",
    Component: lazy(() => import("./features/demo")),
    index: true,
    Layout: HeaderLessShell,
  },
  demoLernreise: {
    scope: "public",
    path: "/demo/:sparkSlug/*",
    getUrl: ({ sparkSlug }) => `/demo/${sparkSlug}`,
    Layout: DemoShell,
    // DemoShell self-hosts its own boundary/suspense (its Layout is the suspending shell).
    noSuspense: true,
  },

  notFoundPage: {
    scope: "public",
    allowMobile: true,
    path: "not-found",
    getUrl: () => "/not-found",
    Component: NotFoundPage,
    Layout: Shell,
  },
  notFound: {
    scope: "public",
    allowMobile: true,
    path: "*",
    Component: NotFoundPage,
    Layout: Shell,
  },
};
